import React, { useEffect, useState } from "react";
import { Bell, Plus, Trash2, Clock, Mail } from "lucide-react";
import {
  emailHygieneService,
  EmailMonitoring,
  MonitoringFrequency,
  WeekDay,
} from "../../services/EmailHygieneService";
import { useToastContext } from "../../context/ToastContext";

interface EmailMonitoringSettingsProps {
  monitoringList: EmailMonitoring[];
  onSelectMonitoring: (monitoring: EmailMonitoring | null) => void;
  onAddMonitoring: () => void;
  onDeleteClick: (e: React.MouseEvent, monitoring: EmailMonitoring) => void;
  onRefresh: () => void;
}

const EmailMonitoringSettings: React.FC<EmailMonitoringSettingsProps> = ({
  monitoringList,
  onSelectMonitoring,
  onAddMonitoring,
  onDeleteClick,
  onRefresh,
}) => {
  const { showToast } = useToastContext();

  const handleUpdateMonitoring = async (
    monitoringId: string,
    isActive: boolean
  ) => {
    try {
      await emailHygieneService.updateMonitoring(monitoringId, { isActive });
      showToast("success", "Monitoring status updated");
      onRefresh();
    } catch (error) {
      showToast("error", "Failed to update monitoring status");
    }
  };

  const handleMonitoringClick = (monitoring: EmailMonitoring) => {
    onSelectMonitoring(monitoring);
  };

  const formatSchedule = (monitoring: EmailMonitoring): string => {
    const time = monitoring.preferredTime;

    switch (monitoring.frequency) {
      case MonitoringFrequency.Daily:
        return `Daily at ${time}`;

      case MonitoringFrequency.Weekly:
        const day = WeekDay[monitoring.preferredDay || 0];
        return `Every ${day} at ${time}`;

      case MonitoringFrequency.Monthly:
        const date = monitoring.preferredDate;
        const suffix =
          date === 1 ? "st" : date === 2 ? "nd" : date === 3 ? "rd" : "th";
        return `Monthly on the ${date}${suffix} at ${time}`;

      default:
        return "";
    }
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-8">Email Monitoring Settings</h2>

        <div className="space-y-4">
          {monitoringList.map((monitoring) => (
            <div
              key={monitoring.id}
              className="bg-background-paper dark:bg-background-paperDark rounded-xl p-6 hover:bg-background-panel dark:hover:bg-background-panelDark cursor-pointer transition-colors"
              onClick={() => handleMonitoringClick(monitoring)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-yellow-DEFAULT" />
                  <div>
                    <h4 className="font-semibold">
                      {monitoring.parameterEmail}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Clock className="w-4 h-4" />
                      <span>{formatSchedule(monitoring)}</span>
                    </div>
                    {monitoring.lastChecked && (
                      <p className="text-sm text-text-secondary mt-1">
                        Last check:{" "}
                        {new Date(monitoring.lastChecked).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateMonitoring(
                          monitoring.id,
                          !monitoring.isActive
                        );
                      }}
                      className={`px-3 py-1 rounded-full text-sm ${
                        monitoring.isActive
                          ? "bg-green-dark/10 text-green-dark"
                          : "bg-red-dark/10 text-red-dark"
                      }`}
                    >
                      {monitoring.isActive ? "Active" : "Inactive"}
                    </button>
                    {monitoring.nextCheckDue && (
                      <span className="text-xs text-text-secondary mt-1">
                        Next check:{" "}
                        {new Date(monitoring.nextCheckDue).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      console.log(
                        "Monitoring being passed to delete:",
                        monitoring
                      );
                      onDeleteClick(e, monitoring);
                    }}
                    className="text-red-dark hover:text-red-dark/80 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {monitoringList.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-DEFAULT/10 rounded-full mb-4">
                <Bell className="w-6 h-6 text-yellow-DEFAULT" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                No Monitoring Configured
              </h3>
              <p className="text-text-secondary mb-6">
                Set up automated checks to monitor your email infrastructure
                health
              </p>
              <button
                onClick={onAddMonitoring}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-DEFAULT hover:bg-yellow-DEFAULT/90 rounded-lg transition-colors mx-auto text-black dark:text-white"
              >
                <Plus className="w-4 h-4" />
                <span>Add Monitoring</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailMonitoringSettings;
